import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate, useLocation } from 'react-router-dom';
import { ChevronLeft, ChevronRight, X, Info } from 'lucide-react';

const LEAVE_TYPE_CONFIG = {
  '연차':           { unit: '반반차(0.25)', desc: '연차 유급 휴가', allowPartial: true, showBalance: true },
  '출산휴가':       { unit: '일', desc: '임신, 출산 등으로 인한 체력회복을 위한 산전 후 유급휴가', allowPartial: false },
  '출산휴가-다태아':{ unit: '일', desc: '임신, 출산 등으로 인한 체력회복을 위한 산전 후 유급휴가', allowPartial: false },
  '배우자돌봄휴가': { unit: '일', desc: '배우자가 출산 후 배우자와 태아의 건강을 위한 휴가', allowPartial: false },
  '가족돌봄휴가':   { unit: '일', desc: '질병, 사고, 양육 등의 이유로 가족을 돌보기 위한 휴가', allowPartial: false },
  '경조사':         { unit: '일', desc: '경조사로 인한 휴가', allowPartial: true, partialTypes: ['종일', '부분'] },
  '공가':           { unit: '일', desc: '공적인 사유로 인한 휴가', allowPartial: true, partialTypes: ['종일', '부분'] },
};

const ALL_TYPES = Object.keys(LEAVE_TYPE_CONFIG);
const TIME_OPTIONS = ['09:00','10:00','11:00','12:00','13:00','14:00','15:00','16:00','17:00','18:00'];

function calcHours(start, end) {
  const s = parseInt(start), e = parseInt(end);
  return Math.max(0, Math.min(e - s, 7));
}

function hoursToDay(h) { return Math.round((h / 8) * 100) / 100; }

function getDateOption(dateObj) {
  const { option, ampm, halfAmpm, startTime, endTime } = dateObj;
  if (option === '종일') return { days: 1, label: '종일' };
  if (option === '반차') return { days: 0.5, label: `반차(${ampm === 'am' ? '오전' : '오후'})` };
  if (option === '반반차') {
    const labels = { 'early-am': '이른오전(09~11)', 'late-am': '늦은오전(11~13)', 'early-pm': '이른오후(13~15)', 'late-pm': '늦은오후(15~17)' };
    return { days: 0.25, label: `반반차(${labels[halfAmpm] || ''})` };
  }
  if (option === '부분') {
    const h = calcHours(startTime || '09:00', endTime || '18:00');
    return { days: hoursToDay(h), label: `부분(${startTime || '09:00'}~${endTime || '18:00'})` };
  }
  return { days: 1, label: '종일' };
}

export default function LeaveNew() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [leaveType, setLeaveType] = useState(location.state?.leaveType || '연차');
  const [dateMode, setDateMode] = useState('pick'); // 'pick' | 'range'
  const [selectedDates, setSelectedDates] = useState([]); // [{date:'2026-04-27', option:'종일', ampm:'am', halfAmpm:'early-am', startTime:'09:00', endTime:'18:00'}]
  const [rangeStart, setRangeStart] = useState('');
  const [rangeEnd, setRangeEnd] = useState('');
  const [reason, setReason] = useState('');
  const [calYear, setCalYear] = useState(new Date().getFullYear());
  const [calMonth, setCalMonth] = useState(new Date().getMonth());
  const [holidays, setHolidays] = useState(new Set());
  const [stats, setStats] = useState(null);
  const [allUsers, setAllUsers] = useState([]);
  const [approvers, setApprovers] = useState([]);
  const [step, setStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);

  const config = LEAVE_TYPE_CONFIG[leaveType] || LEAVE_TYPE_CONFIG['연차'];

  useEffect(() => {
    fetch('/api/leaves/holidays').then(r => r.json()).then(d => setHolidays(new Set(d)));
    fetch(`/api/leaves/stats/${user.id}`).then(r => r.json()).then(setStats);
    fetch('/api/users').then(r => r.json()).then(d => setAllUsers(d.filter(u => u.id !== user.id)));
  }, [user.id]);

  const today = new Date(); today.setHours(0,0,0,0);

  function isDisabled(dateStr) {
    const d = new Date(dateStr); d.setHours(0,0,0,0);
    if (d < today) return true;
    const dow = d.getDay();
    if (dow === 0 || dow === 6) return true;
    if (holidays.has(dateStr)) return true;
    return false;
  }

  function toggleDate(dateStr) {
    if (isDisabled(dateStr)) return;
    if (selectedDates.find(d => d.date === dateStr)) {
      setSelectedDates(prev => prev.filter(d => d.date !== dateStr));
    } else {
      if (selectedDates.length >= 30) { alert('최대 30일까지 선택 가능합니다.'); return; }
      setSelectedDates(prev => [...prev, { date: dateStr, option: '종일', ampm: 'am', halfAmpm: 'early-am', startTime: '09:00', endTime: '18:00' }]);
    }
  }

  function updateDateOption(dateStr, key, val) {
    setSelectedDates(prev => prev.map(d => d.date === dateStr ? { ...d, [key]: val } : d));
  }

  // 총 신청 휴가수 계산
  const totalDays = useMemo(() => {
    if (dateMode === 'range' && rangeStart && rangeEnd) {
      let count = 0;
      const cur = new Date(rangeStart);
      const end = new Date(rangeEnd);
      while (cur <= end) {
        const ds = cur.toISOString().split('T')[0];
        if (!isDisabled(ds)) count++;
        cur.setDate(cur.getDate() + 1);
      }
      return count;
    }
    return selectedDates.reduce((sum, d) => sum + getDateOption(d).days, 0);
  }, [selectedDates, dateMode, rangeStart, rangeEnd, holidays]);

  // 캘린더 생성
  const calDays = useMemo(() => {
    const firstDay = new Date(calYear, calMonth, 1).getDay();
    const daysInMonth = new Date(calYear, calMonth + 1, 0).getDate();
    const days = [];
    for (let i = 0; i < firstDay; i++) days.push(null);
    for (let d = 1; d <= daysInMonth; d++) {
      const pad = String(d).padStart(2, '0');
      const mpad = String(calMonth + 1).padStart(2, '0');
      days.push(`${calYear}-${mpad}-${pad}`);
    }
    return days;
  }, [calYear, calMonth]);

  const isSelected = (ds) => !!selectedDates.find(d => d.date === ds);

  function goSubmit() {
    if (selectedDates.length === 0 && dateMode === 'pick') { alert('날짜를 1일 이상 선택해주세요.'); return; }
    if (dateMode === 'range' && (!rangeStart || !rangeEnd)) { alert('기간을 지정해주세요.'); return; }
    if (approvers.length === 0) { alert('결재자를 1명 이상 선택해주세요.'); return; }
    handleSubmit();
  }

  async function handleSubmit() {
    setSubmitting(true);
    try {
      const sortedDates = [...selectedDates].sort((a,b) => a.date.localeCompare(b.date));
      const dateRange = dateMode === 'range'
        ? `${rangeStart} ~ ${rangeEnd}`
        : sortedDates.length > 0 ? `${sortedDates[0].date} ~ ${sortedDates[sortedDates.length-1].date}` : '';

      const payload = {
        userId: user.id,
        userName: user.name,
        userDept: user.dept,
        userPosition: user.position,
        leaveType,
        dateMode,
        dates: dateMode === 'pick' ? sortedDates : [],
        rangeStart: dateMode === 'range' ? rangeStart : '',
        rangeEnd: dateMode === 'range' ? rangeEnd : '',
        totalDays,
        dateRange,
        reason,
        approvers: approvers.map(a => ({ ...a, status: 'pending', comment: '', date: '' })),
        currentStep: 0,
        author: { id: user.id, name: user.name, dept: user.dept, position: user.position },
      };

      const r = await fetch('/api/leaves', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) });
      const data = await r.json();
      navigate('/leave');
    } catch (e) {
      alert('신청 중 오류가 발생했습니다.');
    } finally {
      setSubmitting(false);
    }
  }

  const toggleApprover = (ap) => {
    setApprovers(prev =>
      prev.find(a => a.id === ap.id)
        ? prev.filter(a => a.id !== ap.id)
        : [...prev, { id: ap.id, name: ap.name, position: ap.position, dept: ap.dept }]
    );
  };

  const getPartialOptions = () => {
    if (leaveType === '연차') return ['종일', '반차', '반반차', '부분'];
    if (config.allowPartial) return ['종일', '부분'];
    return ['종일'];
  };

  return (
    <div className="page container" style={{ maxWidth: 900 }}>
      <div className="page-header" style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <button className="btn btn-secondary btn-sm" onClick={() => navigate('/leave')}>← 뒤로</button>
        <div>
          <h1 className="page-title">휴가 신청</h1>
          <p className="page-subtitle">신청할 휴가와 일자를 선택하여 전자결재 진행 시 휴가 신청이 완료됩니다.</p>
        </div>
      </div>

      {step === 1 && (
        <div className="card">
          {/* 휴가유형 */}
          <div className="form-group">
            <label className="form-label">휴가유형 <span style={{ color: '#ef4444' }}>*</span></label>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <select className="form-select" style={{ maxWidth: 200 }} value={leaveType}
                onChange={e => { setLeaveType(e.target.value); setSelectedDates([]); }}>
                {ALL_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
              <span style={{ padding: '4px 12px', background: '#eff6ff', color: '#3b82f6', borderRadius: 20, fontSize: 12, fontWeight: 600 }}>
                휴가신청단위: {config.unit}
              </span>
            </div>
            <p style={{ fontSize: 12, color: '#9ca3af', marginTop: 6 }}>{config.desc}</p>
            {config.showBalance && stats && (
              <div style={{ padding: '10px 14px', background: '#f9fafb', borderRadius: 8, marginTop: 8, fontSize: 13 }}>
                <span style={{ color: '#6b7280' }}>보유 휴가</span>
                <span style={{ marginLeft: 16, fontWeight: 600 }}>{stats.remainingDays || 0}d ({(stats.remainingDays || 0) * 8}h 0m)</span>
              </div>
            )}
          </div>

          {/* 날짜선택 방식 */}
          <div className="form-group">
            <label className="form-label">휴가신청일 <span style={{ color: '#ef4444' }}>*</span></label>
            <div style={{ display: 'flex', gap: 20, marginBottom: 16 }}>
              {['pick', 'range'].map(m => (
                <label key={m} style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontSize: 14 }}>
                  <input type="radio" checked={dateMode === m} onChange={() => { setDateMode(m); setSelectedDates([]); setRangeStart(''); setRangeEnd(''); }} />
                  {m === 'pick' ? '날짜 선택' : '기간 지정'}
                </label>
              ))}
            </div>

            {dateMode === 'pick' && (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
                {/* 캘린더 */}
                <div style={{ border: '1px solid #e5e7eb', borderRadius: 12, padding: 16 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                    <button onClick={() => { if (calMonth === 0) { setCalYear(y => y - 1); setCalMonth(11); } else setCalMonth(m => m - 1); }}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 18, color: '#374151' }}>‹</button>
                    <span style={{ fontWeight: 600, fontSize: 15 }}>{calYear}년 {calMonth + 1}월</span>
                    <button onClick={() => { if (calMonth === 11) { setCalYear(y => y + 1); setCalMonth(0); } else setCalMonth(m => m + 1); }}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 18, color: '#374151' }}>›</button>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7,1fr)', gap: 2, textAlign: 'center' }}>
                    {['일','월','화','수','목','금','토'].map(d => (
                      <div key={d} style={{ fontSize: 12, color: d === '일' ? '#ef4444' : d === '토' ? '#3b82f6' : '#6b7280', padding: '4px 0', fontWeight: 500 }}>{d}</div>
                    ))}
                    {calDays.map((ds, i) => {
                      if (!ds) return <div key={i} />;
                      const disabled = isDisabled(ds);
                      const selected = isSelected(ds);
                      const dow = new Date(ds).getDay();
                      return (
                        <div key={ds} onClick={() => toggleDate(ds)}
                          style={{ padding: '6px 4px', borderRadius: 20, cursor: disabled ? 'not-allowed' : 'pointer', fontSize: 13, fontWeight: selected ? 700 : 400,
                            background: selected ? '#4f46e5' : 'transparent',
                            color: disabled ? '#d1d5db' : selected ? '#fff' : dow === 0 ? '#ef4444' : dow === 6 ? '#3b82f6' : '#374151',
                            transition: 'all .15s' }}
                          onMouseEnter={e => { if (!disabled && !selected) e.currentTarget.style.background = '#f0f0ff'; }}
                          onMouseLeave={e => { if (!selected) e.currentTarget.style.background = 'transparent'; }}>
                          {parseInt(ds.split('-')[2])}
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* 선택된 날짜 목록 */}
                <div style={{ background: '#f9fafb', borderRadius: 12, padding: 16 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 10, fontSize: 13 }}>
                    <span style={{ fontWeight: 600 }}>신청휴가수</span>
                    <span style={{ color: '#4f46e5', fontWeight: 700 }}>{totalDays}d ({totalDays * 8}h 0m)</span>
                    <span style={{ color: '#ec4899', cursor: 'pointer', fontSize: 12 }}>신청가이드</span>
                  </div>
                  <p style={{ fontSize: 11, color: '#9ca3af', marginBottom: 12 }}>
                    반차, 반반차 등 휴가를 신청하는 경우 옵션을 변경해주세요. 소정근로시간이 같은 기간끼리 휴가신청이 가능합니다.
                  </p>
                  <div style={{ maxHeight: 300, overflowY: 'auto' }}>
                    {[...selectedDates].sort((a,b) => a.date.localeCompare(b.date)).map(d => (
                      <div key={d.date} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, background: '#1f2937', color: '#fff', borderRadius: 20, padding: '4px 10px', fontSize: 12, fontWeight: 600 }}>
                          {`${String(new Date(d.date).getMonth()+1).padStart(2,'0')}/${String(new Date(d.date).getDate()).padStart(2,'0')}`}
                          <button onClick={() => setSelectedDates(prev => prev.filter(x => x.date !== d.date))}
                            style={{ background: 'none', border: 'none', color: '#9ca3af', cursor: 'pointer', fontSize: 14, lineHeight: 1 }}>×</button>
                        </div>
                        {/* 시간 옵션 */}
                        <select value={d.option} onChange={e => updateDateOption(d.date, 'option', e.target.value)}
                          style={{ fontSize: 12, border: '1px solid #e5e7eb', borderRadius: 8, padding: '4px 8px', background: '#f9fafb' }}>
                          {getPartialOptions().map(o => <option key={o} value={o}>{o}</option>)}
                        </select>
                        {d.option === '반차' && (
                          <div style={{ display: 'flex', gap: 4 }}>
                            {[['am','오전'],['pm','오후']].map(([v,l]) => (
                              <button key={v} onClick={() => updateDateOption(d.date, 'ampm', v)}
                                style={{ padding: '3px 10px', borderRadius: 8, border: '1px solid ' + (d.ampm === v ? '#4f46e5' : '#e5e7eb'),
                                  background: d.ampm === v ? '#4f46e5' : '#fff', color: d.ampm === v ? '#fff' : '#374151', fontSize: 11, cursor: 'pointer' }}>{l}</button>
                            ))}
                          </div>
                        )}
                        {d.option === '반반차' && (
                          <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                            {[['early-am','이른오전'],['late-am','늦은오전'],['early-pm','이른오후'],['late-pm','늦은오후']].map(([v,l]) => (
                              <button key={v} onClick={() => updateDateOption(d.date, 'halfAmpm', v)}
                                style={{ padding: '3px 8px', borderRadius: 8, border: '1px solid ' + (d.halfAmpm === v ? '#4f46e5' : '#e5e7eb'),
                                  background: d.halfAmpm === v ? '#4f46e5' : '#fff', color: d.halfAmpm === v ? '#fff' : '#374151', fontSize: 11, cursor: 'pointer' }}>{l}</button>
                            ))}
                          </div>
                        )}
                        {d.option === '부분' && (
                          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                            <select value={d.startTime} onChange={e => updateDateOption(d.date, 'startTime', e.target.value)}
                              style={{ fontSize: 11, border: '1px solid #e5e7eb', borderRadius: 6, padding: '3px' }}>
                              {TIME_OPTIONS.filter(t => t < '18:00').map(t => <option key={t}>{t}</option>)}
                            </select>
                            <span style={{ fontSize: 11, color: '#9ca3af' }}>~</span>
                            <select value={d.endTime} onChange={e => updateDateOption(d.date, 'endTime', e.target.value)}
                              style={{ fontSize: 11, border: '1px solid #e5e7eb', borderRadius: 6, padding: '3px' }}>
                              {TIME_OPTIONS.filter(t => t > (d.startTime || '09:00')).map(t => <option key={t}>{t}</option>)}
                            </select>
                            <span style={{ fontSize: 11, color: '#6b7280' }}>{calcHours(d.startTime || '09:00', d.endTime || '18:00')}h</span>
                          </div>
                        )}
                      </div>
                    ))}
                    {selectedDates.length === 0 && (
                      <p style={{ fontSize: 12, color: '#d1d5db', textAlign: 'center', marginTop: 30 }}>캘린더에서 날짜를 선택하세요</p>
                    )}
                  </div>
                </div>
              </div>
            )}

            {dateMode === 'range' && (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <input type="date" className="form-input" value={rangeStart} min={today.toISOString().split('T')[0]}
                    onChange={e => setRangeStart(e.target.value)} />
                  <span>~</span>
                  <input type="date" className="form-input" value={rangeEnd} min={rangeStart || today.toISOString().split('T')[0]}
                    onChange={e => setRangeEnd(e.target.value)} />
                </div>
                <div style={{ background: '#f9fafb', borderRadius: 12, padding: 16 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginBottom: 8 }}>
                    <span style={{ fontWeight: 600 }}>신청휴가수</span>
                    <span style={{ color: '#4f46e5', fontWeight: 700 }}>{totalDays}d ({totalDays * 8}h 0m)</span>
                  </div>
                  {rangeStart && rangeEnd && (
                    <p style={{ fontSize: 12, color: '#4f46e5' }}>{rangeStart} ~ {rangeEnd}</p>
                  )}
                  <p style={{ fontSize: 11, color: '#9ca3af', marginTop: 8 }}>기간 지정으로 휴가를 신청합니다. 기간지정은 부분 휴가설정을 할 수 없습니다.</p>
                </div>
              </div>
            )}
          </div>

          {/* 신청 사유 */}
          <div className="form-group">
            <label className="form-label">신청 사유</label>
            <textarea className="form-textarea" placeholder="신청 사유를 입력하세요." value={reason} onChange={e => setReason(e.target.value)} style={{ minHeight: 100 }} />
          </div>

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 20 }}>
            <button className="btn btn-secondary" onClick={() => navigate('/leave')}>취소</button>
            <button className="btn btn-primary" onClick={() => setStep(2)}>다음 단계 (결재선 설정) ›</button>
          </div>
        </div>
      )}

      {step === 2 && (
        <>
          <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
            <button className="btn btn-secondary btn-sm" onClick={() => setStep(1)}>← 뒤로</button>
          </div>
          {/* 작성내용 요약 */}
          <div className="card" style={{ marginBottom: 20 }}>
            <h3 style={{ fontSize: 15, fontWeight: 600, marginBottom: 12 }}>신청 내용 확인</h3>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2,1fr)', gap: '6px 24px' }}>
              <div style={{ display: 'flex', gap: 8, fontSize: 13, padding: '6px 0', borderBottom: '1px solid #f9fafb' }}>
                <span style={{ color: '#9ca3af', minWidth: 80 }}>휴가유형</span>
                <span style={{ fontWeight: 500 }}>{leaveType}</span>
              </div>
              <div style={{ display: 'flex', gap: 8, fontSize: 13, padding: '6px 0', borderBottom: '1px solid #f9fafb' }}>
                <span style={{ color: '#9ca3af', minWidth: 80 }}>신청휴가수</span>
                <span style={{ fontWeight: 500, color: '#4f46e5' }}>{totalDays}d ({totalDays * 8}h 0m)</span>
              </div>
            </div>
            {dateMode === 'pick' && selectedDates.length > 0 && (
              <div style={{ marginTop: 12, padding: 12, background: '#f9fafb', borderRadius: 8 }}>
                <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 8 }}>휴가신청일 ({selectedDates.length}일)</div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  {[...selectedDates].sort((a,b) => a.date.localeCompare(b.date)).map(d => {
                    const dow = ['일','월','화','수','목','금','토'][new Date(d.date).getDay()];
                    const opt = getDateOption(d);
                    return (
                      <div key={d.date} style={{ fontSize: 12, color: '#3b82f6', padding: '2px 8px', background: '#eff6ff', borderRadius: 4 }}>
                        {d.date}({dow}) - {opt.label}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
            {dateMode === 'range' && rangeStart && rangeEnd && (
              <div style={{ marginTop: 12, padding: 12, background: '#f9fafb', borderRadius: 8, fontSize: 13, color: '#3b82f6' }}>
                {rangeStart} ~ {rangeEnd}
              </div>
            )}
            {reason && (
              <div style={{ marginTop: 12, fontSize: 13 }}>
                <span style={{ color: '#9ca3af' }}>신청사유: </span>{reason}
              </div>
            )}
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
            <div className="card">
              <h3 style={{ fontSize: 15, fontWeight: 600, marginBottom: 6 }}>결재선 설정 *</h3>
              <p style={{ fontSize: 12, color: '#9ca3af', marginBottom: 14 }}>클릭하여 순서대로 결재자를 추가하세요</p>
              {allUsers.map(ap => {
                const selected = approvers.find(a => a.id === ap.id);
                const order = approvers.findIndex(a => a.id === ap.id) + 1;
                return (
                  <div key={ap.id} onClick={() => toggleApprover(ap)}
                    style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', borderRadius: 8, cursor: 'pointer', marginBottom: 6,
                      background: selected ? '#f0f0ff' : '#f9fafb', border: `1.5px solid ${selected ? '#4f46e5' : 'transparent'}` }}>
                    <div className="avatar" style={{ width: 32, height: 32, fontSize: 12, flexShrink: 0, background: selected ? '#4f46e5' : '#d1d5db', color: '#fff' }}>
                      {selected ? order : ap.name[0]}
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 13, fontWeight: 500 }}>{ap.name}</div>
                      <div style={{ fontSize: 11, color: '#9ca3af' }}>{ap.dept} · {ap.position}</div>
                    </div>
                    {selected && <span style={{ fontSize: 11, color: '#4f46e5', fontWeight: 600 }}>{order}순위</span>}
                  </div>
                );
              })}
            </div>
            <div>
              {approvers.length > 0 && (
                <div className="card" style={{ marginBottom: 16 }}>
                  <h3 style={{ fontSize: 14, fontWeight: 600, marginBottom: 12 }}>결재 흐름</h3>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
                      <div className="avatar" style={{ width: 36, height: 36, fontSize: 12, background: '#6b7280' }}>{user.name[0]}</div>
                      <span style={{ fontSize: 11, color: '#6b7280' }}>기안</span>
                    </div>
                    {approvers.map((ap, i) => (
                      <React.Fragment key={ap.id}>
                        <span style={{ color: '#d1d5db', fontSize: 20 }}>›</span>
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
                          <div className="avatar" style={{ width: 36, height: 36, fontSize: 12, background: '#4f46e5' }}>{ap.name[0]}</div>
                          <span style={{ fontSize: 11, color: '#4f46e5', fontWeight: 600 }}>{i+1}차</span>
                        </div>
                      </React.Fragment>
                    ))}
                  </div>
                </div>
              )}
              <div className="card" style={{ marginBottom: 16 }}>
                <h3 style={{ fontSize: 14, fontWeight: 600, marginBottom: 10 }}>기안자</h3>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <div className="avatar" style={{ width: 36, height: 36, fontSize: 13 }}>{user.name[0]}</div>
                  <div>
                    <div style={{ fontSize: 14, fontWeight: 500 }}>{user.name}</div>
                    <div style={{ fontSize: 12, color: '#9ca3af' }}>{user.dept} · {user.position}</div>
                  </div>
                </div>
              </div>
              <button className="btn btn-primary" style={{ width: '100%', justifyContent: 'center', padding: 13, fontSize: 15 }}
                onClick={goSubmit} disabled={submitting}>
                {submitting ? '신청 중...' : '✅ 전자결재 상신'}
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
